import React, { useState, useEffect, useRef } from 'react';
import '../App.css';
import './EnhancedCodeBrowser.css';
import { GitHubService, GitHubFile, GitHubRepo } from '../services/github';
import FileTree from './FileTree';
import CodeEditor from './CodeEditor';
import { CodeAnalysisDashboard } from './CodeAnalysisDashboard';
import { ProfessionalCard, ProfessionalButton, StatusIndicator, MetricCard } from './ProfessionalUI';

// Default demo repository
const DEFAULT_DEMO_REPO = {
  name: 'nanoGPT',
  url: 'https://github.com/karpathy/nanoGPT',
  description: 'The simplest, fastest repository for training/finetuning medium-sized GPTs',
  language: 'Python',
  icon: '🚀'
};

// Demo repositories for quick access
const DEMO_REPOSITORIES = [
  DEFAULT_DEMO_REPO,
  {
    name: 'React',
    url: 'https://github.com/facebook/react',
    description: 'A JavaScript library for building user interfaces',
    language: 'JavaScript',
    icon: '⚛️'
  },
  {
    name: 'TensorFlow',
    url: 'https://github.com/tensorflow/tensorflow',
    description: 'An Open Source Machine Learning Framework',
    language: 'C++',
    icon: '🧠'
  },
  {
    name: 'Vue.js',
    url: 'https://github.com/vuejs/core',
    description: 'Progressive JavaScript framework',
    language: 'TypeScript',
    icon: '💚'
  },
  {
    name: 'VS Code',
    url: 'https://github.com/microsoft/vscode',
    description: 'Visual Studio Code',
    language: 'TypeScript',
    icon: '💻'
  }
];

const CodeBrowser: React.FC = () => {
  const [gitHubService] = useState(() => new GitHubService());
  const [repoUrl, setRepoUrl] = useState('');
  const [repo, setRepo] = useState<GitHubRepo | null>(null);
  const [files, setFiles] = useState<GitHubFile[]>([]);
  const [selectedFile, setSelectedFile] = useState<GitHubFile | null>(null);
  const [fileContent, setFileContent] = useState('');
  const [language, setLanguage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expandedFolders] = useState<Set<string>>(new Set());
  const [aiAnalysis, setAiAnalysis] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [showAdvancedAnalysis, setShowAdvancedAnalysis] = useState(false);

  // 三栏布局宽度状态（百分比）
  const [leftWidth, setLeftWidth] = useState(33.33);
  const [middleWidth, setMiddleWidth] = useState(33.33);
  const [rightWidth, setRightWidth] = useState(33.34);
  
  // 拖拽状态
  const [isDragging, setIsDragging] = useState<'left' | 'right' | null>(null);
  const [dragStartX, setDragStartX] = useState(0);
  const [dragStartLeftWidth, setDragStartLeftWidth] = useState(0);
  const [dragStartMiddleWidth, setDragStartMiddleWidth] = useState(0);
  const [dragStartRightWidth, setDragStartRightWidth] = useState(0);

  // 容器引用，用于获取容器宽度
  const containerRef = useRef<HTMLDivElement>(null);

  // Auto-load default demo repository on component mount
  useEffect(() => {
    console.log('=== Auto-load effect ===');
    console.log('Current repo:', repo);
    console.log('Current loading:', loading);
    console.log('DEFAULT_DEMO_REPO.url:', DEFAULT_DEMO_REPO.url);

    if (!repo && !loading) {
      console.log('Setting repo URL to default demo:', DEFAULT_DEMO_REPO.url);
      setRepoUrl(DEFAULT_DEMO_REPO.url);
      // Trigger form submission immediately after setting URL
      setTimeout(() => {
        const form = document.getElementById('repo-form') as HTMLFormElement;
        console.log('Form found:', !!form);
        if (form) {
          console.log('Dispatching submit event...');
          const submitEvent = new Event('submit', { cancelable: true });
          form.dispatchEvent(submitEvent);
        }
      }, 100);
    }
  }, [repo, loading]);

  // Debug effect to track selectedFile changes
  useEffect(() => {
    console.log('=== Selected File Changed ===');
    console.log('selectedFile:', selectedFile);
    console.log('fileContent length:', fileContent.length);
    console.log('language:', language);
    console.log('loading:', loading);
  }, [selectedFile, fileContent, language, loading]);

  // Debug render tracking
  useEffect(() => {
    console.log('=== CodeBrowser Rendered ===');
    console.log('Current state:');
    console.log('- selectedFile:', selectedFile?.name || 'null');
    console.log('- fileContent length:', fileContent.length);
    console.log('- language:', language);
    console.log('- loading:', loading);
    console.log('- initialLoading:', initialLoading);
  });

  const handleDemoRepoClick = (demoUrl: string) => {
    setRepoUrl(demoUrl);
    // Auto-submit after setting URL
    setTimeout(() => {
      const form = document.getElementById('repo-form') as HTMLFormElement;
      if (form) {
        const submitEvent = new Event('submit', { cancelable: true });
        form.dispatchEvent(submitEvent);
      }
    }, 100);
  };

  const analyzeCode = async () => {
    if (!selectedFile || !fileContent) return;

    setAiLoading(true);
    setAiAnalysis('');

    // Simulate AI analysis (in a real app, this would call an AI service)
    setTimeout(() => {
      const analysis = generateCodeAnalysis(fileContent, language, selectedFile.name);
      setAiAnalysis(analysis);
      setAiLoading(false);
    }, 2000);
  };

  const generateCodeAnalysis = (content: string, lang: string, fileName: string) => {
    // Simple analysis generation (in a real app, use AI service like Claude API)
    const lines = content.split('\n').length;
    const functions = (content.match(/function\s+\w+|const\s+\w+\s*=\s*\([^)]*\)\s*=>/g) || []).length;
    const classes = (content.match(/class\s+\w+/g) || []).length;

    return `# 📋 代码结构分析 - ${fileName}

## 📊 基本信息
- **文件类型**: ${lang}
- **代码行数**: ${lines} 行
- **函数数量**: ${functions} 个
- **类数量**: ${classes} 个

## 🏗️ 结构概览
${lang === 'JavaScript' || lang === 'TypeScript' ? `
这个文件使用现代 JavaScript/TypeScript 语法编写，包含了：
- ES6+ 语法特性
- 模块化结构
- 函数式编程风格
` : lang === 'Python' ? `
这个文件使用 Python 语法编写，特点是：
- 简洁的语法结构
- 强大的内置功能
- 易于理解的代码逻辑
` : `
这个文件使用 ${lang} 编写，具有专业的代码结构和良好的组织方式。
`}

## 💡 代码特点
- **可读性**: 代码结构清晰，命名规范
- **维护性**: 逻辑分离明确，便于维护
- **扩展性**: 设计考虑了未来可能的扩展需求

## 🎯 核心功能
${functions > 0 ? `
文件定义了 ${functions} 个主要函数，每个函数都有明确的职责：
- 参数处理和验证
- 核心业务逻辑实现
- 错误处理和边界情况
` : '这个文件主要包含配置或数据结构定义。'}

## 📚 学习要点
1. **代码组织**: 观察代码是如何组织和分层的
2. **命名规范**: 注意变量和函数的命名方式
3. **错误处理**: 学习如何优雅地处理异常情况
4. **性能考虑**: 分析代码的性能优化点

## 🔍 深度解读
这个文件展示了 ${lang} 的最佳实践，包括：
- 现代语法特性的运用
- 清晰的代码结构
- 良好的编程习惯
`;
  };

  const handleUrlSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('=== handleUrlSubmit called ===');
    console.log('repoUrl:', repoUrl);

    if (!repoUrl.trim()) {
      console.log('Empty repo URL error');
      setError('Please enter a GitHub repository URL');
      return;
    }

    const parsed = gitHubService.parseGitHubUrl(repoUrl);
    console.log('Parsed URL:', parsed);

    if (!parsed) {
      console.log('Invalid repo URL error');
      setError('Invalid GitHub repository URL');
      return;
    }

    setLoading(true);
    setError('');
    console.log('Starting to load repo...');

    try {
      // 获取仓库信息
      console.log('Fetching repo info for:', parsed.owner, parsed.repo);
      const repoInfo = await gitHubService.getRepoInfo(parsed.owner, parsed.repo);
      console.log('Repo info received:', repoInfo.name, repoInfo.stars, 'stars');
      setRepo(repoInfo);

      // 获取仓库文件列表
      console.log('Fetching repo contents...');
      const repoFiles = await gitHubService.getRepoContents(parsed.owner, parsed.repo);
      console.log('Repo files received:', repoFiles.length, 'files');
      setFiles(repoFiles);
      setSelectedFile(null);
      setFileContent('');
      setInitialLoading(false);
      console.log('Repo loading completed successfully');
    } catch (err: any) {
      console.error('Error loading repo:', err);
      setError(err.message || 'Failed to load repository');
      setInitialLoading(false);
    } finally {
      setLoading(false);
      console.log('Loading finished');
    }
  };

  const handleFileSelect = async (file: GitHubFile) => {
    console.log('=== File Select Debug ===');
    console.log('File selected:', file);
    console.log('Repo URL:', repoUrl);
    console.log('Repo info:', repo);

    const parsed = repoUrl ? gitHubService.parseGitHubUrl(repoUrl) : null;
    console.log('Parsed URL:', parsed);

    if (!parsed || !repo) {
      console.error('No repo URL or repo info available');
      setError('仓库信息不完整，请重新加载仓库');
      return;
    }

    if (file.type === 'file') {
      console.log('Setting selected file:', file.name);
      setSelectedFile(file);
      const language = gitHubService.getLanguageFromPath(file.path);
      console.log('Detected language:', language);
      setLanguage(language);

      try {
        console.log('Fetching file content...');
        console.log('Owner:', parsed.owner, 'Repo:', parsed.repo, 'Path:', file.path);
        const content = await gitHubService.getFileContent(parsed.owner, parsed.repo, file.path);
        console.log('File content fetched successfully, length:', content.length);
        console.log('Content preview:', content.substring(0, 100) + '...');

        setFileContent(content);
        setError(''); // Clear any previous errors

        // Auto-trigger AI analysis when file is selected
        setTimeout(() => {
          console.log('Triggering AI analysis...');
          analyzeCode();
        }, 500);
      } catch (err: any) {
        console.error('Error fetching file content:', err);
        const errorMessage = `无法加载文件内容: ${err.message || '未知错误'}`;
        setError(errorMessage);
        setFileContent(`// ${errorMessage}`);
      }
    } else {
      console.log('Folder selected, skipping content fetch');
    }
    console.log('=== End File Select Debug ===');
  };

  const handleFolderExpand = async (folderPath: string) => {
    const parsed = repoUrl ? gitHubService.parseGitHubUrl(repoUrl) : null;
    if (!parsed || !repo) return;

    // 检查是否已经加载过这个文件夹的内容
    const folderFiles = files.filter(f => f.path.startsWith(folderPath + '/'));
    if (folderFiles.length === 0) {
      try {
        setLoading(true);
        const subFiles = await gitHubService.getRepoContents(parsed.owner, parsed.repo, folderPath);
        setFiles(prev => [...prev, ...subFiles]);
        // 展开文件夹状态由FileTree组件内部管理
      } catch (err: any) {
        setError(err.message || 'Failed to load folder contents');
      } finally {
        setLoading(false);
      }
    }
    // 如果已经有内容，不重复加载，展开状态由FileTree组件管理
  };

  // 拖拽处理函数
  const handleMouseDown = (side: 'left' | 'right', e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(side);
    setDragStartX(e.clientX);
    setDragStartLeftWidth(leftWidth);
    setDragStartMiddleWidth(middleWidth);
    setDragStartRightWidth(rightWidth);
    // 禁用文本选择
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';
  };

  // 全局鼠标移动和释放事件处理
  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!containerRef.current) return;

      const containerWidth = containerRef.current.clientWidth;
      const deltaX = e.clientX - dragStartX;
      const deltaPercent = (deltaX / containerWidth) * 100;

      if (isDragging === 'left') {
        // 拖拽左侧分隔线：调整左侧栏和中间栏的宽度，右侧栏自动调整以保持总和100%
        const newLeftWidth = Math.max(15, Math.min(50, dragStartLeftWidth + deltaPercent));
        const newMiddleWidth = Math.max(15, Math.min(70, dragStartMiddleWidth - deltaPercent));
        
        if (newLeftWidth >= 15 && newMiddleWidth >= 15) {
          const remainingWidth = 100 - newLeftWidth - newMiddleWidth;
          if (remainingWidth >= 15) {
            setLeftWidth(newLeftWidth);
            setMiddleWidth(newMiddleWidth);
            setRightWidth(remainingWidth);
          }
        }
      } else if (isDragging === 'right') {
        // 拖拽右侧分隔线：调整中间栏和右侧栏的宽度，左侧栏自动调整以保持总和100%
        const newMiddleWidth = Math.max(15, Math.min(70, dragStartMiddleWidth + deltaPercent));
        const newRightWidth = Math.max(15, Math.min(50, dragStartRightWidth - deltaPercent));
        
        if (newMiddleWidth >= 15 && newRightWidth >= 15) {
          const remainingWidth = 100 - newMiddleWidth - newRightWidth;
          if (remainingWidth >= 15) {
            setMiddleWidth(newMiddleWidth);
            setRightWidth(newRightWidth);
            setLeftWidth(remainingWidth);
          }
        }
      }
    };

    const handleMouseUp = () => {
      setIsDragging(null);
      // 恢复文本选择和光标
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragStartX, dragStartLeftWidth, dragStartMiddleWidth, dragStartRightWidth, leftWidth, middleWidth, rightWidth]);

  return (
    <div className="h-screen bg-gray-900 flex flex-col">
      {/* Header */}
      <header className="glass-effect border-b border-gray-700 p-6 slide-up">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-3xl font-bold gradient-text mb-6 fade-in">CodePlato</h1>

          {/* URL输入表单 - 改进版 */}
          <form id="repo-form" onSubmit={handleUrlSubmit} className="flex gap-4 fade-in">
            <div className="flex-1 relative group">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400 group-focus-within:text-blue-400 transition-colors" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M12.316 3.051a1 1 0 01.633 1.265l-4 12a1 1 0 11-1.898-.632l4-12a1 1 0 011.265-.633zM5.707 6.293a1 1 0 010 1.414L3.414 10l2.293 2.293a1 1 0 11-1.414 1.414l-3-3a1 1 0 010-1.414l3-3a1 1 0 011.414 0zm8.586 0a1 1 0 011.414 0l3 3a1 1 0 010 1.414l-3 3a1 1 0 11-1.414-1.414L16.586 10l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </div>
              <input
                id="repo-url-input"
                name="repoUrl"
                type="text"
                value={repoUrl}
                onChange={(e) => setRepoUrl(e.target.value)}
                placeholder="Enter GitHub repository URL (e.g., https://github.com/facebook/react)"
                className="w-full pl-12 pr-4 py-3.5 bg-gradient-to-r from-gray-800 to-gray-700 text-white rounded-xl border-2 border-gray-600 focus:outline-none focus:border-blue-500 focus:shadow-lg focus:shadow-blue-500/20 transition-all duration-300 hover:border-gray-500 placeholder-gray-400"
                autoComplete="url"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="px-8 py-3.5 bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-xl hover:from-blue-500 hover:to-blue-400 disabled:from-gray-700 disabled:to-gray-600 disabled:cursor-not-allowed transition-all duration-300 font-semibold shadow-lg shadow-blue-600/30 hover:shadow-xl hover:shadow-blue-500/40 hover:scale-105 active:scale-95"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                  </svg>
                  Loading...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  Browse
                </span>
              )}
            </button>
          </form>

          {/* 错误信息 */}
          {error && (
            <div className="mt-3 p-3 bg-red-900/20 border border-red-500/30 rounded-lg text-red-300 text-sm fade-in">
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                {error}
              </div>
            </div>
          )}

          {/* 仓库信息 */}
          {repo && (
            <div className="mt-6 p-4 bg-gray-800/50 border border-gray-700 rounded-lg fade-in">
              <div className="flex flex-wrap items-center gap-6 text-sm">
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M2 5a2 2 0 012-2h12a2 2 0 012 2v10a2 2 0 01-2 2H4a2 2 0 01-2-2V5zm3.293 1.293a1 1 0 011.414 0l3 3a1 1 0 010 1.414l-3 3a1 1 0 01-1.414-1.414L7.586 10 5.293 7.707a1 1 0 010-1.414zM11 12a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd" />
                  </svg>
                  <span className="text-gray-400">Repository:</span>
                  <span className="text-white font-medium">{repo.full_name}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 bg-gradient-to-r from-yellow-400 to-orange-400 rounded-full"></span>
                  <span className="text-gray-400">Language:</span>
                  <span>{repo.language || 'N/A'}</span>
                </div>
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                  <span className="text-yellow-400 font-medium">{repo.stars.toLocaleString()}</span>
                </div>
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M12.316 3.051a1 1 0 01.633 1.265l-4 12a1 1 0 11-1.898-.632l4-12a1 1 0 011.265-.633zM5.707 6.293a1 1 0 010 1.414L3.414 10l2.293 2.293a1 1 0 11-1.414 1.414l-3-3a1 1 0 010-1.414l3-3a1 1 0 011.414 0zm8.586 0a1 1 0 011.414 0l3 3a1 1 0 010 1.414l-3 3a1 1 0 11-1.414-1.414L16.586 10l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                  <span className="text-green-400 font-medium">{repo.forks.toLocaleString()}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* 主要内容区域 - 始终显示三栏布局 - 支持拖拽调整 */}
      <div ref={containerRef} className="flex-1 flex overflow-hidden">
        {/* 左侧栏：文件树 */}
        <aside
          className="border-r border-gray-700 bg-gray-800/30 backdrop-blur-sm flex flex-col min-w-0"
          style={{ width: `${leftWidth}%` }}
        >
          {/* 文件树区域 */}
          <div className="flex-1 overflow-hidden">
            <div className="p-4 border-b border-gray-700">
              <h3 className="text-sm font-semibold text-gray-300 uppercase tracking-wider mb-2">
                📁 文件浏览器 {repo && `- ${repo.name}`}
              </h3>
            </div>
            {initialLoading || loading ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <div className="w-12 h-12 mx-auto mb-4 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                  <p className="text-gray-400 text-sm">
                    {initialLoading ? '正在加载 nanoGPT 项目...' : '加载文件中...'}
                  </p>
                </div>
              </div>
            ) : repo ? (
              <FileTree
                files={files}
                onFileSelect={handleFileSelect}
                onFolderExpand={handleFolderExpand}
                loading={loading}
                expandedFolders={expandedFolders}
              />
            ) : (
              <div className="flex items-center justify-center h-full">
                <div className="text-center text-gray-500">
                  <svg className="w-16 h-16 mx-auto mb-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
                  </svg>
                  <p className="text-sm">暂无仓库</p>
                </div>
              </div>
            )}
          </div>
        </aside>

        {/* 左侧分隔线 - 可拖拽 */}
        <div
          className={`relative group ${
            isDragging === 'left' ? 'bg-blue-500' : 'bg-gray-700'
          } transition-colors`}
          onMouseDown={(e) => handleMouseDown('left', e)}
          style={{ cursor: 'col-resize', userSelect: 'none', width: '4px' }}
        >
          {/* 增加可点击区域的视觉提示和交互区域 */}
          <div className="absolute inset-y-0 -left-2 -right-2 bg-transparent hover:bg-blue-500/10 transition-colors" />
          <div className="absolute inset-y-0 left-1/2 -translate-x-1/2 w-1 h-full group-hover:bg-blue-500 transition-colors" />
        </div>

        {/* 中间：代码显示区 - 始终显示代码编辑器 */}
        <main 
          className="overflow-hidden bg-gray-900/50 min-w-0"
          style={{ width: `${middleWidth}%` }}
        >
            {initialLoading || loading ? (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <div className="w-16 h-16 mx-auto mb-4 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                  <p className="text-gray-400 text-lg mb-2">
                    {initialLoading ? '正在加载 nanoGPT 项目...' : '加载文件中...'}
                  </p>
                  {initialLoading && (
                    <p className="text-sm text-gray-500">
                      Andrej Karpathy 的最小 GPT 实现
                    </p>
                  )}
                </div>
              </div>
            ) : selectedFile ? (
              <CodeEditor
                file={selectedFile}
                content={fileContent}
                language={language}
                loading={loading}
              />
            ) : repo ? (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center text-gray-500">
                  <svg className="w-24 h-24 mx-auto mb-6 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                  </svg>
                  <h3 className="text-xl font-semibold text-white mb-2">{repo.name}</h3>
                  <p className="text-gray-400 mb-4">{repo.description || '请从左侧选择文件查看代码'}</p>
                  <div className="flex justify-center gap-6 text-sm">
                    <div className="flex items-center gap-2">
                      <span className="w-3 h-3 bg-gradient-to-r from-yellow-400 to-orange-400 rounded-full"></span>
                      <span className="text-gray-400">{repo.language || 'N/A'}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <svg className="w-4 h-4 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                      <span className="text-yellow-400">{repo.stars.toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center text-gray-500">
                  <svg className="w-24 h-24 mx-auto mb-6 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-lg">请从左侧选择 Demo 项目</p>
                  <p className="text-sm text-gray-400 mt-2">或输入 GitHub 仓库 URL</p>
                </div>
              </div>
            )}
        </main>

        {/* 右侧分隔线 - 可拖拽 */}
        <div
          className={`relative group ${
            isDragging === 'right' ? 'bg-blue-500' : 'bg-gray-700'
          } transition-colors`}
          onMouseDown={(e) => handleMouseDown('right', e)}
          style={{ cursor: 'col-resize', userSelect: 'none', width: '4px' }}
        >
          {/* 增加可点击区域的视觉提示和交互区域 */}
          <div className="absolute inset-y-0 -left-2 -right-2 bg-transparent hover:bg-blue-500/10 transition-colors" />
          <div className="absolute inset-y-0 left-1/2 -translate-x-1/2 w-1 h-full group-hover:bg-blue-500 transition-colors" />
        </div>

        {/* 右侧栏：Demo 样例 + AI 代码分析 */}
        <aside
          className="border-l border-gray-700 bg-gray-800/30 backdrop-blur-sm flex flex-col min-w-0"
          style={{ width: `${rightWidth}%` }}
        >
          {/* Demo 样例区域 - 移到顶部 */}
          <div className="border-b border-gray-700 p-4 bg-gradient-to-br from-gray-800 to-gray-900">
            <h3 className="text-sm font-semibold text-white uppercase tracking-wider mb-3 flex items-center gap-2">
              <span className="text-lg">🚀</span>
              Demo 代码样例
            </h3>
            <div className="space-y-2 max-h-64 overflow-y-auto custom-scrollbar">
              {DEMO_REPOSITORIES.map((demo, index) => (
                <button
                  key={index}
                  onClick={() => handleDemoRepoClick(demo.url)}
                  className={`w-full text-left p-3 rounded-lg border transition-all duration-200 group ${
                    repo && repo.full_name === demo.url.replace('https://github.com/', '')
                      ? 'bg-gradient-to-r from-blue-600/40 to-purple-600/40 border-blue-400 shadow-lg shadow-blue-500/20'
                      : 'bg-gray-700/90 hover:bg-gray-600/90 border-gray-500 hover:border-blue-400/70 hover:shadow-md'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className="text-lg">{demo.icon}</span>
                    <span className={`font-bold text-sm group-hover:text-blue-200 transition-colors ${
                      repo && repo.full_name === demo.url.replace('https://github.com/', '')
                        ? 'text-blue-200'
                        : 'text-black'
                    }`}>
                      {demo.name}
                    </span>
                  </div>
                  <div className="text-xs font-bold text-black leading-relaxed mb-1">{demo.description}</div>
                  <div className="text-xs font-semibold text-blue-200 mt-1.5 flex items-center gap-1.5">
                    <span className="w-2 h-2 bg-blue-300 rounded-full shadow-sm shadow-blue-400/50"></span>
                    {demo.language}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* 分析模式切换 */}
          <div className="p-4 border-b border-gray-700">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-gray-300 uppercase tracking-wider">🤖 AI 代码分析</h3>
              <StatusIndicator
                status={selectedFile ? 'success' : 'info'}
                text={selectedFile ? '已就绪' : '等待文件'}
                variant="badge"
              />
            </div>

            {selectedFile && (
              <div className="flex gap-2 mt-3">
                <ProfessionalButton
                  size="sm"
                  variant={showAdvancedAnalysis ? 'primary' : 'ghost'}
                  onClick={() => setShowAdvancedAnalysis(true)}
                  className="flex-1"
                >
                  深度分析
                </ProfessionalButton>
                <ProfessionalButton
                  size="sm"
                  variant={!showAdvancedAnalysis ? 'primary' : 'ghost'}
                  onClick={() => setShowAdvancedAnalysis(false)}
                  className="flex-1"
                >
                  快速分析
                </ProfessionalButton>
              </div>
            )}
          </div>

          <div className="flex-1 overflow-y-auto">
            {showAdvancedAnalysis && selectedFile ? (
              <div className="p-4">
                <CodeAnalysisDashboard
                  code={fileContent}
                  language={language}
                  fileName={selectedFile.name}
                  onAnalysisComplete={(result) => {
                    console.log('Advanced analysis completed:', result);
                  }}
                />
              </div>
            ) : (
              <div className="p-4">
                {initialLoading || loading ? (
                  <ProfessionalCard variant="glass" className="loading-card">
                    <div className="loading-content">
                      <div className="loading-spinner"></div>
                      <p className="text-gray-400 text-sm">等待代码加载...</p>
                    </div>
                  </ProfessionalCard>
                ) : aiLoading ? (
                  <ProfessionalCard variant="glass" className="loading-card">
                    <div className="loading-content">
                      <div className="loading-spinner"></div>
                      <p className="text-gray-400 text-sm">AI 正在分析代码...</p>
                      <div className="loading-progress">
                        <div className="progress-dots">
                          <span></span>
                          <span></span>
                          <span></span>
                        </div>
                      </div>
                    </div>
                  </ProfessionalCard>
                ) : selectedFile ? (
                  <div className="analysis-content">
                    {/* 文件信息卡片 */}
                    <ProfessionalCard
                      title="文件信息"
                      icon="📄"
                      variant="elevated"
                      className="mb-4"
                    >
                      <div className="file-info">
                        <div className="info-row">
                          <span className="info-label">文件名</span>
                          <span className="info-value">{selectedFile.name}</span>
                        </div>
                        <div className="info-row">
                          <span className="info-label">语言</span>
                          <span className="info-value">{language}</span>
                        </div>
                        <div className="info-row">
                          <span className="info-label">大小</span>
                          <span className="info-value">{selectedFile.size ? `${(selectedFile.size / 1024).toFixed(1)} KB` : 'N/A'}</span>
                        </div>
                        <div className="info-row">
                          <span className="info-label">路径</span>
                          <span className="info-value text-xs">{selectedFile.path}</span>
                        </div>
                      </div>
                    </ProfessionalCard>

                    {/* 代码统计 */}
                    <ProfessionalCard
                      title="代码统计"
                      icon="📊"
                      variant="gradient"
                      className="mb-4"
                    >
                      <div className="code-stats">
                        <MetricCard
                          title="代码行数"
                          value={fileContent.split('\n').length}
                          icon="📝"
                          size="sm"
                          variant="default"
                        />
                        <MetricCard
                          title="字符数"
                          value={fileContent.length.toLocaleString()}
                          icon="🔤"
                          size="sm"
                          variant="default"
                        />
                      </div>
                    </ProfessionalCard>

                    {/* 分析结果 */}
                    {aiAnalysis && (
                      <ProfessionalCard
                        title="AI 分析结果"
                        icon="🧠"
                        variant="glass"
                        className="mb-4"
                        headerAction={
                          <ProfessionalButton
                            onClick={analyzeCode}
                            variant="ghost"
                            size="sm"
                            icon="🔄"
                          >
                            重新分析
                          </ProfessionalButton>
                        }
                      >
                        <div className="analysis-result">
                          <div className="whitespace-pre-wrap text-gray-300 text-sm leading-relaxed">
                            {aiAnalysis}
                          </div>
                        </div>
                      </ProfessionalCard>
                    )}

                    {/* 分析工具 */}
                    <div className="analysis-tools">
                      <h4 className="text-sm font-semibold text-gray-300 mb-3">🔧 分析工具</h4>
                      <div className="tools-grid">
                        <ProfessionalCard variant="elevated" className="tool-card">
                          <div className="tool-icon">🎯</div>
                          <div className="tool-name">代码解释</div>
                          <div className="tool-desc">智能解读代码逻辑</div>
                        </ProfessionalCard>
                        <ProfessionalCard variant="elevated" className="tool-card">
                          <div className="tool-icon">🔗</div>
                          <div className="tool-name">调用链分析</div>
                          <div className="tool-desc">函数依赖关系图</div>
                        </ProfessionalCard>
                        <ProfessionalCard variant="elevated" className="tool-card">
                          <div className="tool-icon">⚡</div>
                          <div className="tool-name">性能分析</div>
                          <div className="tool-desc">代码性能评估</div>
                        </ProfessionalCard>
                        <ProfessionalCard variant="elevated" className="tool-card">
                          <div className="tool-icon">🛡️</div>
                          <div className="tool-name">安全检查</div>
                          <div className="tool-desc">安全漏洞扫描</div>
                        </ProfessionalCard>
                      </div>
                    </div>
                  </div>
                ) : (
                  <ProfessionalCard variant="glass">
                    <div className="empty-state">
                      <div className="empty-icon">📄</div>
                      <h4 className="text-sm font-semibold text-gray-300 mb-2">选择文件开始分析</h4>
                      <p className="text-xs text-gray-400">从左侧文件树中选择一个文件，CodePlato AI 将为您深度解析代码结构</p>

                      {/* 快速操作 */}
                      <div className="quick-actions mt-4">
                        <h5 className="text-xs font-semibold text-gray-400 mb-2">快速操作</h5>
                        <div className="action-buttons">
                          <ProfessionalButton variant="ghost" size="sm" className="action-btn">
                            📂 浏览示例
                          </ProfessionalButton>
                          <ProfessionalButton variant="ghost" size="sm" className="action-btn">
                            📚 查看帮助
                          </ProfessionalButton>
                        </div>
                      </div>
                    </div>
                  </ProfessionalCard>
                )}
              </div>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
};

export default CodeBrowser;